import type { FixtureState } from '../components/OtherPlayerCallsGrid'
import type { SimGameStatus } from '../gameweekSimulation'
import type { Game } from '../types'

type ScoreLine = { homeScore: number; awayScore: number }

/**
 * Per-fixture kickoff/score state for another player's cards.
 *
 * The section as a whole is gated on the gameweek deadline; within it each fixture reads its own
 * state, so a Saturday lunchtime game is settled while the Sunday ones are still blank. Three
 * sources, in the order they become true: a revealed result, a fixture the simulation has live,
 * and one the simulation has final.
 *
 * Shared by the visited profile's `GW N Calls` section and the Predict page's top-scorer modal —
 * two surfaces showing the same player's same week must not disagree about which fixtures have
 * started.
 */
export function buildOtherPlayerFixtureStates({
  games,
  revealedResults,
  simGameStatuses,
  actualResults,
  liveResults,
}: {
  games: Game[]
  revealedResults: Record<string, ScoreLine>
  simGameStatuses?: Record<string, SimGameStatus>
  actualResults: Record<string, ScoreLine>
  liveResults?: Record<string, ScoreLine>
}): Record<string, FixtureState> {
  const states: Record<string, FixtureState> = {}
  for (const game of games) {
    const revealed = revealedResults[game.id]
    const simStatus = simGameStatuses?.[game.id]
    if (revealed) {
      states[game.id] = { kickedOff: true, score: revealed }
    } else if (simStatus === 'live') {
      states[game.id] = { kickedOff: true, score: liveResults?.[game.id] }
    } else if (simStatus === 'final') {
      states[game.id] = { kickedOff: true, score: actualResults[game.id] }
    } else {
      states[game.id] = { kickedOff: false }
    }
  }
  return states
}
